import { subscribeTelemetry, getRecentTelemetry } from '../services/api/telemetry';
import { getCurrentUser } from '../services/api/auth';
import type { RemoteTelemetry } from '../services/api/telemetry';

export class MultiSessionDashboard {
  private overlay: HTMLElement;
  private panel: HTMLElement;
  private tableBody: HTMLElement;
  private statusBadge: HTMLElement;
  private counterEl: HTMLElement;
  private pauseBtn: HTMLButtonElement;
  private paused = false;
  private samples: RemoteTelemetry[] = [];
  private maxSamples = 50;
  private unsubscribe: (() => void) | null = null;
  private insertedSince = 0;

  constructor(container: HTMLElement) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'auth-overlay hidden';

    this.panel = document.createElement('div');
    this.panel.className = 'live-dashboard';
    this.panel.innerHTML = `
      <div class="live-dashboard-header">
        <div class="live-dashboard-title-row">
          <h3><span class="live-dot">●</span> Painel Ao Vivo</h3>
          <span id="liveStatusBadge" class="live-status-badge">Conectado</span>
        </div>
        <div class="live-dashboard-toolbar">
          <span id="liveCounter" class="live-counter">0 amostras</span>
          <label class="live-filter-label">
            Dispositivo:
            <select id="liveFilterSelect" class="live-filter-select">
              <option value="">Todos</option>
            </select>
          </label>
          <button id="liveRefreshBtn" class="btn btn-sm btn-secondary" title="Buscar histórico recente">Atualizar</button>
          <button id="livePauseBtn" class="btn btn-sm btn-secondary">Pausar</button>
          <button id="liveCloseBtn" class="auth-modal-close">&times;</button>
        </div>
      </div>
      <div class="live-dashboard-body">
        <table class="live-table">
          <thead>
            <tr>
              <th>Hora</th>
              <th>Dispositivo</th>
              <th>Freq (Hz)</th>
              <th>Corrente (A)</th>
              <th>DC (V)</th>
              <th>Temp (°C)</th>
              <th>Carga (%)</th>
            </tr>
          </thead>
          <tbody id="liveTableBody"></tbody>
        </table>
        <div id="liveEmptyState" class="live-empty">Aguardando amostras...</div>
      </div>
    `;

    container.appendChild(this.overlay);
    container.appendChild(this.panel);

    this.tableBody = this.panel.querySelector('#liveTableBody')!;
    this.statusBadge = this.panel.querySelector('#liveStatusBadge')!;
    this.counterEl = this.panel.querySelector('#liveCounter')!;
    this.pauseBtn = this.panel.querySelector('#livePauseBtn') as HTMLButtonElement;

    const closeBtn = this.panel.querySelector('#liveCloseBtn')!;
    const filterSelect = this.panel.querySelector('#liveFilterSelect') as HTMLSelectElement;
    const refreshBtn = this.panel.querySelector('#liveRefreshBtn') as HTMLButtonElement;

    this.overlay.addEventListener('click', () => this.close());
    closeBtn.addEventListener('click', () => this.close());

    this.pauseBtn.addEventListener('click', () => {
      this.paused = !this.paused;
      this.pauseBtn.textContent = this.paused ? 'Retomar' : 'Pausar';
      this.pauseBtn.classList.toggle('btn-secondary', !this.paused);
      this.pauseBtn.classList.toggle('btn-primary', this.paused);
    });

    filterSelect.addEventListener('change', () => this.render());

    refreshBtn.addEventListener('click', () => this.fetchRecent());
  }

  open(): void {
    this.overlay.classList.remove('hidden');
    this.panel.classList.add('open');
    this.fetchRecent();
    this.startListening();
  }

  close(): void {
    this.overlay.classList.add('hidden');
    this.panel.classList.remove('open');
    this.stopListening();
  }

  destroy(): void {
    this.stopListening();
    this.overlay.remove();
    this.panel.remove();
  }

  private startListening(): void {
    this.insertedSince = Date.now();
    getCurrentUser().then(user => {
      this.unsubscribe = subscribeTelemetry((sample) => {
        this.handleTelemetry(sample);
      }, user?.id);
    });
  }

  private async fetchRecent(): Promise<void> {
    const samples = await getRecentTelemetry(50);
    if (samples.length > 0) {
      this.samples = samples;
      this.insertedSince = Date.now();
      this.render();
      this.updateCounter();
    }
  }

  private stopListening(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  private handleTelemetry(sample: RemoteTelemetry): void {
    if (this.paused) return;
    this.samples.unshift(sample);
    if (this.samples.length > this.maxSamples) this.samples.pop();
    this.render();
    this.updateCounter();
  }

  private updateCounter(): void {
    const elapsed = (Date.now() - this.insertedSince) / 1000;
    if (elapsed > 0) {
      const perMin = Math.round((this.samples.length / elapsed) * 60);
      this.counterEl.textContent = `${this.samples.length} amostras · ${perMin}/min`;
    }
  }

  private render(): void {
    const filterSelect = this.panel.querySelector('#liveFilterSelect') as HTMLSelectElement;
    const filterValue = filterSelect?.value ?? '';

    // Build device list for filter
    const deviceSet = new Set<string>();
    for (const s of this.samples) {
      if (s.profile_key) deviceSet.add(s.profile_key);
    }
    const currentOptions = Array.from(filterSelect?.options ?? []).map(o => o.value);
    const newDevices = Array.from(deviceSet).filter(d => !currentOptions.includes(d));
    if (filterSelect && newDevices.length > 0) {
      for (const d of newDevices) {
        const opt = document.createElement('option');
        opt.value = d;
        opt.textContent = d;
        filterSelect.appendChild(opt);
      }
    }

    const filtered = filterValue
      ? this.samples.filter(s => s.profile_key === filterValue)
      : this.samples;

    const emptyEl = this.panel.querySelector('#liveEmptyState') as HTMLElement;
    if (filtered.length === 0) {
      this.tableBody.innerHTML = '';
      if (emptyEl) emptyEl.style.display = 'block';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';

    this.tableBody.innerHTML = filtered.map(s => {
      const time = new Date(s.timestamp).toLocaleTimeString('pt-BR');
      return `
        <tr>
          <td class="live-cell-time">${time}</td>
          <td class="live-cell-device">${this.escapeHtml(s.profile_key)}</td>
          <td class="live-cell-num">${s.freq?.toFixed(1) ?? '-'}</td>
          <td class="live-cell-num">${s.current_amp?.toFixed(2) ?? '-'}</td>
          <td class="live-cell-num">${s.dc_voltage?.toFixed(0) ?? '-'}</td>
          <td class="live-cell-num">${s.temp?.toFixed(1) ?? '-'}</td>
          <td class="live-cell-num">${s.load_pct?.toFixed(0) ?? '-'}</td>
        </tr>
      `;
    }).join('');

    this.statusBadge.textContent = '🟢 Ao Vivo';
  }

  private escapeHtml(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
}
